import React, { useState, useRef, useEffect } from 'react';
import { apiClient, ENDPOINTS } from '../services/apiConfig';
import { useAuth } from '../context/AuthContext'; // Import auth context
import { 
  Select,
  FormControl,
  InputAdornment,
  Drawer,
  ListItemButton,
} from '@mui/material';
import {
  // ... existing imports ...
  
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  DateRange as DateRangeIcon,
  Label as LabelIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Edit as EditIcon,
  Brush as BrushIcon,
  Mic as MicIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Videocam as VideocamIcon,
  PlayArrow as PlayArrowIcon,
  SelfImprovement as SelfImprovementIcon,
  OndemandVideo as OndemandVideoIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

// Mock data for inspiration prompts
const inspirationPrompts = [
  "Draw something that represents how you feel today using only three colors.",
  "Write a short poem about a moment that brought you joy recently.",
  "Create a sketch that represents your current emotional state.",
  "Record a one-minute audio reflection on what's bringing you peace right now.",
  "Write a letter to your future self about your current feelings.",
  "Draw a landscape that matches your emotional weather today.",
  "Create a character that embodies how you're feeling right now.",
  "Record yourself humming or singing a tune that matches your mood.",
  "Let your hand move freely to create patterns and designs that represent your emotional journey",
  "Describe a place or moment that reflects your mood, using vivid details and sounds.",
  "Speak positive affirmations to yourself and replay them when needed.",
  "Write about a challenge you're facing, but from a third-person perspective.",
  "List three things you're grateful for and describe why they bring you joy.",
  "Record your dreams and explore any emotions or symbols within them.",
  "List three qualities you love about yourself and expand on how they support you.",
  "Sketch an abstract representation of your emotional journey this week.",
  "Close your eyes and let your body express what you're feeling right now, without judgment.",
  "Imagine you're a breeze flowing through the air. How does your movement change as you become stronger or softer?",
  "Tune in to the rhythm of your heartbeat and let it guide your movements.",
  "Move like water, fire, air, or earth—explore their different textures and energies through dance.",
  "Try moving as slowly as possible, feeling every muscle engage with purpose.",
  "Let gentle swaying soothe your body like a comforting embrace.",
  "Create symbols that represent strength, healing, and hope in your life.",
  "Sketch something from nature that reflects your mood, like a storm for frustration or a sunrise for hope.",
  "Depict two sides of yourself having a conversation through sketches.",
];

// Mock data for user Crafts
const mockCrafts = [
  {
    id: 1,
    title: "Morning Reflections",
    type: "writing",
    content: "Today I woke up feeling surprisingly energetic despite the rainy weather...",
    createdAt: "2025-06-10T08:30:00",

    likes: 5,
    comments: 2,
  },
  {
    id: 2,
    title: "Anxiety Sketch",
    type: "drawing",
    content: "/images/sketch1.jpg", // This would be a path to the image
    createdAt: "2025-06-08T15:45:00",

    likes: 0,
    comments: 0,
  },
  {
    id: 3,
    title: "Voice Note - Feeling Calm",
    type: "audio",
    content: "/audio/calm_note.mp3", // This would be a path to the audio file
    createdAt: "2025-06-05T19:20:00",


    likes: 12,
    comments: 4,
  },
];



const CreativeOutlet = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [Crafts, setCrafts] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState(inspirationPrompts[0]);
  const [isCreating, setIsCreating] = useState(false);
  const [creationType, setCreationType] = useState<'writing' | 'drawing' | 'audio' | 'dancing' | 'imaging'>('writing');
  const [imagingPrompt, setImagingPrompt] = useState('');
  const [imagingImageURL, setImagingImageURL] = useState('');
  const [newCraft, setNewCraft] = useState({
    title: '',
    content: '',
  });
  const auth = useAuth(); // Get auth context
  const user = auth?.currentUser; // Get current user from auth context
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false); 
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [CraftFilter, setCraftFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [CraftTags, setCraftTags] = useState<{[key: number]: string[]}>({});
  const [newTag, setNewTag] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState(9);
  
  // Reset pagination when filters/search/sort change
  useEffect(() => {
    setVisibleCount(9);
  }, [searchTerm, CraftFilter, sortBy, selectedTags, dateFilter, categoryFilter]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCraft, setSelectedCraft] = useState<typeof mockCrafts[0] | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);

  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize] = useState(5);
  
  // Get a new inspiration prompt
  const getNewPrompt = () => {
    const randomIndex = Math.floor(Math.random() * inspirationPrompts.length);
    setCurrentPrompt(inspirationPrompts[randomIndex]);
  };

  // Generate AI Imaging based on prompt
  const generateImaging = async () => {
    const trimmed = imagingPrompt.trim();
    const words = trimmed ? trimmed.split(/\s+/) : [];
    if (!trimmed) {
      setSnackbarMessage('Please enter a prompt first');
      setSnackbarOpen(true);
      return;
    }
    if (words.length > 1000) {
      setSnackbarMessage('Prompt must be 1000 words or fewer');
      setSnackbarOpen(true);
      return;
    }
    try {
      setLoading(true);
      const resp = await apiClient.post(ENDPOINTS.AI_IMAGE, { prompt: trimmed });
      const url = resp?.data?.url;
      if (!url) {
        throw new Error('No image URL returned');
      }
      setImagingImageURL(url);
      setNewCraft({ ...newCraft, content: url });
      setCreationType('imaging');
      setSnackbarMessage('Image generated successfully!');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Imaging generation error:', err?.response?.data || err?.message);
      setSnackbarMessage('Failed to generate image. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
   // Dancing state
   const [danceMode, setDanceMode] = useState<'watch' | 'self'>('watch');
   const [showCamera, setShowCamera] = useState(false);
   const [recordingVideo, setRecordingVideo] = useState(false);
   const [videoURL, setVideoURL] = useState<string>('');
   const videoRef = useRef<HTMLVideoElement>(null);
   const mediaRecorderRef = useRef<MediaRecorder | null>(null);
   const videoChunksRef = useRef<Blob[]>([]);
   const recordedVideoBlobRef = useRef<Blob | null>(null);

   const blobToDataURL = (blob: Blob) =>
     new Promise<string>((resolve) => {
       const reader = new FileReader();
       reader.onloadend = () => resolve(reader.result as string);
       reader.readAsDataURL(blob);
     });

   // MediaRecorder MIME support helpers
   const supportedMimeType = (kind: 'audio' | 'video') => {
     const candidates =
       kind === 'audio'
         ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
         : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
     return typeof MediaRecorder !== 'undefined'
       ? candidates.find((t) => MediaRecorder.isTypeSupported(t))
       : undefined;
   };
   // eslint-disable-next-line 
   const extFromMime = (mime?: string) => {    
     if (!mime) return 'webm';
     if (mime.includes('webm')) return 'webm';
     if (mime.includes('mp4')) return 'mp4';
     if (mime.includes('ogg')) return 'ogg';
     return 'webm';
   };
   const videoMimeRef = useRef<string | undefined>(undefined);
   const audioMimeRef = useRef<string | undefined>(undefined);
   
   // Dance videos
   const danceVideos = [
     { 
       id: 1, 
       title: 'Mindful Movement Flow', 
       description: 'A gentle dance routine to express emotions through movement',
       url: 'https://www.youtube.com/embed/UBMk30rjy0o',
       duration: '5 min'
     },
     { 
       id: 2, 
       title: 'Expressive Dance Therapy', 
       description: 'Use dance as a form of emotional release and self-expression',
       url: 'https://www.youtube.com/embed/lc1Ag9m7XQo',
       duration: '8 min'
     },
     { 
       id: 3, 
       title: 'Mood-Boosting Dance Routine', 
       description: 'Simple dance moves to lift your spirits and energy',
       url: 'https://www.youtube.com/embed/yN3GgCUmmXw',
       duration: '10 min'
     },
   ];

   const startSelfDancing = async () => {
    setDanceMode('self');
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
        const p = videoRef.current.play();
        if (p && typeof p.then === 'function') {
          p.catch(() => {});
        }
      }
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      setSnackbarMessage('Error accessing camera. Please check permissions.');
      setSnackbarOpen(true);
      setShowCamera(false);
    }
  };
  
  // Start recording dance video
  const startRecordingVideo = () => {
    if (!videoRef.current?.srcObject) return;

    const stream = videoRef.current.srcObject as MediaStream;
    const chosen = supportedMimeType('video');
    const mediaRecorder = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    videoMimeRef.current = chosen;
    videoChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        videoChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const type =
        videoMimeRef.current ||
        (videoChunksRef.current[0] && videoChunksRef.current[0].type) ||
        'video/webm';
      const videoBlob = new Blob(videoChunksRef.current, { type });
      recordedVideoBlobRef.current = videoBlob;

      const url = URL.createObjectURL(videoBlob);
      setVideoURL(url);

      // Keep preview URL in content for immediate UI; we’ll persist data in saveCraft
      setNewCraft({
        ...newCraft,
        content: url,
      });

      setSnackbarMessage('Dance video recorded successfully!');
      setSnackbarOpen(true);
    };

    mediaRecorder.start();
    setRecordingVideo(true);
  };

  // Upload captured media blob and return server URL
  async function uploadBlob(blob: Blob, baseName: string): Promise<string> {
    const formData = new FormData();
    const ext = (() => {
      const t = blob.type || '';
      if (t.includes('webm')) return 'webm';
      if (t.includes('mp4')) return 'mp4';
      if (t.includes('wav')) return 'wav';
      if (t.includes('mpeg') || t.includes('mp3')) return 'mp3';
      if (t.includes('aac')) return 'aac';
      if (t.includes('quicktime')) return 'mov';
      return '';
    })();
    const filename = ext ? `${baseName}.${ext}` : baseName;
    formData.append('file', blob, filename);
    const resp = await apiClient.post(ENDPOINTS.UPLOAD_MEDIA, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp.data.url as string;
  }
  
  // Stop recording dance video
  const stopRecordingVideo = () => {
    if (mediaRecorderRef.current && recordingVideo) {
      mediaRecorderRef.current.stop();
      setShowCamera(false);
    }
  };
  
  // Close camera
  const closeCamera = () => {
    if (recordingVideo) {
      stopRecordingVideo();
      setShowCamera(false);
    }
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();      
      tracks.forEach(track => {
        track.stop();
      });
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  // Start creating a new Craft
  const startCreating = (type: 'writing' | 'drawing' | 'audio'| 'dancing' | 'imaging') => {
    setCreationType(type);
    setIsCreating(true);
    setNewCraft({
      title: '',
      content: '',
      
    });
    
    // If drawing, initialize canvas
    if (type === 'drawing' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    // If imaging, reset prompt and preview
    if (type === 'imaging') {
      setImagingPrompt('');
      setImagingImageURL('');
    }
  };
  
  // Save the new Craft to database
  const saveCraft = async () => {
    if (!newCraft.title) {
      setSnackbarMessage('Please add a title for your Craft');
      setSnackbarOpen(true);
      return;
    }

    // Ensure user is logged in (protected routes)
    const token = localStorage.getItem('token');
    if (!token) {
      setSnackbarMessage('Please log in to save your Craft');
      setSnackbarOpen(true);
      return;
    }

    // Preflight: ensure content or recording exists
    if (creationType === 'writing' && (!newCraft.content || newCraft.content.trim() === '')) {
      setSnackbarMessage('Please add some text for your writing Craft');
      setSnackbarOpen(true);
      return;
    }
    if (creationType === 'dancing' && !recordedVideoBlobRef.current && !videoURL) {
      setSnackbarMessage('Please finish recording your video before saving');
      setSnackbarOpen(true);
      return;
    }
    if (creationType === 'audio' && !audioBlob && !newCraft.content) {
      setSnackbarMessage('Please finish recording your audio before saving');
      setSnackbarOpen(true);
      return;
    }
    if (creationType === 'imaging' && !newCraft.content) {
      setSnackbarMessage('Please generate an image before saving');
      setSnackbarOpen(true);
      return;
    }

    let content = newCraft.content;

    // For drawing, get the canvas data URL
    if (creationType === 'drawing' && canvasRef.current) {
      content = canvasRef.current.toDataURL();
    }

    // Persist recorded video via upload (fallback to base64 on failure)
    if (creationType === 'dancing') {
      if (recordedVideoBlobRef.current) {
        try {
          content = await uploadBlob(recordedVideoBlobRef.current, `video_${Date.now()}`);
        } catch (e) {
          console.error('Error uploading video:', e);
          content = await blobToDataURL(recordedVideoBlobRef.current);
        }
      } else if (!content && videoURL) {
        content = videoURL; // temporary preview fallback
      }
    }

    // Persist recorded audio via upload (fallback to base64 on failure)
    if (creationType === 'audio' && audioBlob) {
      try {
        content = await uploadBlob(audioBlob, `audio_${Date.now()}`);
      } catch (e) {
        console.error('Error uploading audio:', e);
        content = await blobToDataURL(audioBlob);
      }
    }

    // For imaging, content is the URL returned by backend; no extra handling needed.

    try {
      setLoading(true);

      const CraftData = {
        title: newCraft.title,
        type: creationType,
        content,
        tags: CraftTags[Date.now()] || []
      };

      // Interceptor already adds Authorization header
      const response = await apiClient.post(ENDPOINTS.CREATIVE_CRAFTS, CraftData);

      setCrafts(prevCrafts => [response.data, ...prevCrafts] as typeof prevCrafts);
      setIsCreating(false);
      setSnackbarMessage('Craft saved successfully!');
      setSnackbarOpen(true);
      setLoading(false);
    } catch (error: any) {
      console.error('Error saving Craft:', {
        status: error?.response?.status,
        // Shows the fully resolved request URL for quicker debugging
        url: error?.response?.config?.url || `${apiClient.defaults.baseURL}${ENDPOINTS.CREATIVE_CRAFTS}`,
        data: error?.response?.data,
        message: error?.message
      });

      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;

      if (status === 401) {
        setSnackbarMessage('Session expired or not logged in. Please log in.');
      } else if (status === 404) {
        setSnackbarMessage('Save route not found. Check API path and server.');
      } else if (status === 400 && serverMessage) {
        setSnackbarMessage(serverMessage);
      } else {
        setSnackbarMessage('Failed to save Craft. Please try again later while ensure the current recording is finished.');
      }

      setSnackbarOpen(true);
      setLoading(false);
    }
  };
  
  const downloadImaging = async () => {
    if (creationType !== 'imaging') return;

    const url =
      imagingImageURL ||
      (typeof newCraft.content === 'string' ? newCraft.content : '');

    if (!url) return;

    const filename = `image-${new Date().toISOString()}.png`;

    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  // Download the recorded dance video on Dancing tab
  // eslint-disable-next-line
  const downloadDancing = () => {
    if (creationType !== 'dancing') return;

    const blob = recordedVideoBlobRef.current;
    const mime = videoMimeRef.current || (blob && blob.type) || 'video/webm';
    const ext = extFromMime(mime) || 'webm';
    const filename = `dance-${new Date().toISOString()}.${ext}`;

    let href: string | null = null;
    let revokeLater = false;

    if (blob) {
      href = URL.createObjectURL(blob);
      revokeLater = true;
    } else if (videoURL) {
      href = videoURL;
    }

    if (!href) {
      setSnackbarMessage('No video to download');
      setSnackbarOpen(true);
      return;
    }

    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    if (revokeLater) {
      setTimeout(() => URL.revokeObjectURL(href as string), 5000);
    }
  };
  
  // Cancel creating a new Craft
  const cancelCreating = () => {
    setIsCreating(false);
    setShowCamera(false);
  };
  

  
  // Delete a Craft from database
  const deleteCraft = async (CraftId: string | number) => {
    try {      
      setLoading(true);      
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Configure headers with token
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      console.log('Deleting Craft entry with ID:', CraftId);
      
      // Send delete request to backend API
      //await axios.delete(`http://localhost:5000/api/creative-Crafts/${CraftId}`, config);
      await apiClient.delete(`${ENDPOINTS.CREATIVE_CRAFTS}/${CraftId}`, config);
      
      // Remove from state - handle both string and number IDs
      setCrafts(Crafts.filter(Craft => {
        // Handle both string and number IDs
        const craftId = (Craft as any).id || (Craft as any)._id;
        return craftId !== CraftId;
      }));
      setSnackbarMessage('Craft deleted successfully');
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error('Error deleting Craft:', error);
      setSnackbarMessage('Failed to delete Craft. Please try again.');
      setSnackbarOpen(true);      
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening the detail view
  const handleViewCraft = (craft: typeof mockCrafts[0]) => {
    setSelectedCraft(craft);
    setDetailDialogOpen(true);
  };
  

  
  // Drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    setIsDrawing(true);
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.strokeStyle = brushColor;
  };
  
  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.closePath();
    setIsDrawing(false);
  };
  
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string>('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState<NodeJS.Timeout | null>(null);
  

// audioChunksRef is already declared above, so we can remove this duplicate declaration

  // Fetch user's Crafts on component mount
  useEffect(() => {
    const fetchCrafts = async () => {
      if (user) {
        try {
          setLoading(true);

          // Get token from localStorage
          const token = localStorage.getItem('token');
          
          // Configure headers with token
          const config = {
            headers: {
              Authorization: `Bearer ${token}`
            }
          };

          // Send get request to backend API
          //const response = await axios.get('http://localhost:5000/api/creative-Crafts', config);
          const response = await apiClient.get(ENDPOINTS.CREATIVE_CRAFTS, config);
          setCrafts(response.data);
          
          // Initialize Craft tags from fetched data
          const tagsObj = {};
          response.data.forEach((Craft: {id: number, tags?: string[]}) => {
            if (Craft.tags && Craft.tags.length > 0) {
              (tagsObj as {[key: number]: string[]})[Craft.id] = Craft.tags;
            }
          });
          setCraftTags(tagsObj);
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching Crafts:', error);
          setLoading(false);
        }
      }
    };
    
    fetchCrafts();
  }, [user]);
  
const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Audio recording functions
  const startRecording = async () => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const chosen = supportedMimeType('audio');
      const mediaRecorder = chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioMimeRef.current = chosen;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const type =
          audioMimeRef.current ||
          (audioChunksRef.current[0] && audioChunksRef.current[0].type) ||
          'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type });
        setAudioBlob(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
        setNewCraft({ ...newCraft, content: audioUrl });
        setSnackbarMessage('Audio recorded successfully!');
        setSnackbarOpen(true);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      const timer = setInterval(() => setRecordingTime((prevTime) => prevTime + 1), 1000);
      setRecordingTimer(timer);
    };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Clear recording timer
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    return () => {
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [recordingTimer, audioURL]);

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  

const handleAddTag = (CraftId: number, tag: string) => {
  if (!tag.trim()) return;
  
  setCraftTags(prev => {
    const currentTags = prev[CraftId] || [];
    if (currentTags.includes(tag.trim())) return prev;
    
    return {
      ...prev,
      [CraftId]: [...currentTags, tag.trim()]
    };
  });
  
  setNewTag('');
  setSnackbarMessage('Tag added successfully!');
  setSnackbarOpen(true);
};

const handleRemoveTag = (CraftId: number, tagToRemove: string) => {
  setCraftTags(prev => ({
    ...prev,
    [CraftId]: (prev[CraftId] || []).filter(tag => tag !== tagToRemove)
  }));
};

// Get all unique tags across Crafts
const getAllTags = () => {
  const allTags = new Set<string>();
  Object.values(CraftTags).forEach(tags => {
    tags.forEach(tag => allTags.add(tag));
  });
  return Array.from(allTags);
};

// Filter and sort Crafts based on current filters and sort options
const getFilteredAndSortedCrafts = () => {
  return Crafts
    .filter(Craft => {
      // Filter by search term
      if (searchTerm && !((Craft as {title: string}).title) && typeof (Craft as {title: string}).title === 'string' && !((Craft as {title: string}).title).toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Filter by Craft type
      if (CraftFilter !== 'all' && (Craft as {type: string}).type !== CraftFilter) {
        return false;
      }
      
      // Filter by tags
      if (selectedTags.length > 0) {
        const CraftTagList = CraftTags[(Craft as {id: number}).id] || [];
        if (!selectedTags.some(tag => CraftTagList.includes(tag))) {
          return false;
        }
      }
      
      // Filter by date
      if (dateFilter !== 'all') {
        const CraftDate = new Date((Craft as {createdAt: string}).createdAt);
        const today = new Date();
        
        if (dateFilter === 'today') {
          return CraftDate.toDateString() === today.toDateString();
        } else if (dateFilter === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(today.getDate() - 7);
          return CraftDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date();
          monthAgo.setMonth(today.getMonth() - 1);
          return CraftDate >= monthAgo;
        }
      }
      
      // Filter by category
      if (categoryFilter.length > 0) {
        // This would be implemented with actual categories
        // For now, we'll use the Craft type as a stand-in for category
        return categoryFilter.includes((Craft as {type: string}).type);
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date((b as {createdAt: string}).createdAt).getTime() - new Date((a as {createdAt: string}).createdAt).getTime();
      } else if (sortBy === 'oldest') {
        return new Date((a as {createdAt: string}).createdAt).getTime() - new Date((b as {createdAt: string}).createdAt).getTime();
      } else if (sortBy === 'title') {
        return (a as {title: string}).title.localeCompare((b as {title: string}).title);
      } else if (sortBy === 'popular') {
        return (b as {likes: number}).likes - (a as {likes: number}).likes;
      }
      return 0;
    });
};

// Filter drawer component
const FilterDrawer = () => {
  return (
    <Drawer
      anchor="right"
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
    >
      <Box sx={{ width: 300, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Advanced Filters</Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Date Created
        </Typography>
        <List>
          {['all', 'today', 'week', 'month'].map((option) => (
            <ListItemButton
              key={option}
              selected={dateFilter === option}
              onClick={() => setDateFilter(option as 'all' | 'today' | 'week' | 'month')}
            >
              <ListItemIcon>
                <DateRangeIcon />
              </ListItemIcon>
              <ListItemText 
                primary={option === 'all' ? 'All Time' : 
                        option === 'today' ? 'Today' : 
                        option === 'week' ? 'This Week' : 
                        'This Month'} 
              />
            </ListItemButton>
          ))}
        </List>
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Tags
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {getAllTags().map(tag => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(prev => prev.filter(t => t !== tag));
                } else {
                  setSelectedTags(prev => [...prev, tag]);
                }
              }}
              sx={{
                bgcolor: selectedTags.includes(tag) ? 'rgba(108, 141, 250, 0.2)' : 'rgba(108, 141, 250, 0.05)',
                color: selectedTags.includes(tag) ? '#6C8DFA' : 'text.secondary',
                borderRadius: 2
              }}
            />
          ))}
          {getAllTags().length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No tags created yet
            </Typography>
          )}
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            onClick={() => {
              setDateFilter('all');
              setSelectedTags([]);
              setCategoryFilter([]);
              setFilterDrawerOpen(false);
            }}
            sx={{ mr: 1 }}
          >
            Reset
          </Button>
          <Button 
            variant="contained"
            onClick={() => setFilterDrawerOpen(false)}
            sx={{ 
              background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
              borderRadius: 2,
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};


  // Render Craft card based on type
  const renderCraftContent = (Craft: typeof mockCrafts[0]) => {
    switch (Craft.type) {
      case 'writing':
        return (
          <Typography variant="body2" color="text.secondary">
            {Craft.content.length > 150 
              ? Craft.content.substring(0, 150) + '...' 
              : Craft.content}
          </Typography>
        );
      case 'drawing':
        return (
          <Box 
            component="img" 
            src={Craft.content} 
            alt={Craft.title}
            sx={{ 
              width: '100%', 
              height: 150, 
              objectFit: 'cover',
              borderRadius: 1,
            }}
          />
        );
      case 'audio':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <audio controls src={Craft.content} style={{ width: '100%' }}>
              Your browser does not support the audio element.
            </audio>
          </Box>
        );
      case 'imaging':
        return (
          <Box 
            component="img" 
            src={Craft.content} 
            alt={Craft.title}
            sx={{ 
              width: '100%', 
              height: 150, 
              objectFit: 'cover',
              borderRadius: 1,
            }}
          />
        );
        case 'dancing':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <video controls src={Craft.content} style={{ width: '100%', height: 150, objectFit: 'cover' }}>
                Your browser does not support the video element.
              </video>
            </Box>
          );
      default:
        return null;
    }
  };

  const location = useLocation();
  const emotionData = location.state as { emotion?: string; intensity?: number } || {};
  const getSuggestedActivities = (emotion: string, intensity: number) => {
    if (emotion === 'Frustrated' || emotion === 'Angry') {
      if (intensity >= 7) {
        return [
          "Scribble your frustration, then tear it up.",
          "Use bold, expressive strokes with red and black colors.",
          "Create a physical representation of your stress and destroy it.",
          "Write a letter expressing your feelings (that you won't send)."
        ];
      } else {
        return [
          "Draw abstract shapes that represent your feelings.",
          "Create a mind map of what's bothering you.",
          "Try a guided breathing exercise while sketching.",
          "Write a short story with a character who overcomes similar feelings."
        ];
      }
    }
    
    // Default suggestions
    return [
      "Free-form drawing or painting",
      "Journal writing",
      "Collage making",
      "Poetry writing"
    ];
  };
  
  const suggestedActivities = emotionData.emotion 
    ? getSuggestedActivities(emotionData.emotion, emotionData.intensity || 5)
    : [];
  

  return (
    <>  
       <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>      
      {emotionData.emotion && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 3 },
            mb: { xs: 3, md: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(108, 141, 250, 0.1) 0%, rgba(255, 159, 126, 0.1) 100%)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, fontFamily: '"Lato", sans-serif' }}>
            Creative Exercises for {emotionData.emotion} Feelings
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, fontFamily: '"Quicksand", sans-serif' }}>
            We've noticed you're feeling {emotionData.emotion.toLowerCase()} today. 
            Here are some creative activities that might help:
          </Typography>
          
          <Grid container spacing={2}>
            {suggestedActivities.map((activity, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500, mb: 1, fontFamily: '"Lato", sans-serif' }}>
                      Activity {index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                      {activity}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small">Try This</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
     </Container> 


      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: '#4A306D', fontFamily: '"Lato", sans-serif', fontSize: { xs: 26, sm: 32 } }}>
          Your Mood Craft
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#4A306D', opacity: 0.8, fontFamily: '"Lato", sans-serif' }}>
          Express yourself creatively and track your emotional journey.            
          Share your creative expressions with the world. <br /><br />
        </Typography>

        
        {/* Inspiration Prompt */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            mb: { xs: 3, md: 4 }, 
            borderRadius: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(108, 141, 250, 0.1) 0%, rgba(255, 159, 126, 0.1) 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 0 },
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
              Inspiration:
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ fontStyle: 'italic', mt: 1, fontFamily: '"Quicksand", sans-serif', overflowWrap: 'anywhere' }}>
            "{currentPrompt}"
          </Typography>
             <Button startIcon={<RefreshIcon />} onClick={getNewPrompt} sx={{ color: '#4A306D', width: { xs: '100%', sm: 'auto' } }}>
              New Idea
            </Button>
        </Paper>
        
        {/* Create New Craft Buttons */}
        {!isCreating && (
          <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => startCreating('writing')}
              sx={{ 
                bgcolor: '#6C8DFA',
                '&:hover': { bgcolor: '#5A7DE0' },
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Write
            </Button>
            <Button
              variant="contained"
              startIcon={<BrushIcon />}
              onClick={() => startCreating('drawing')}
              sx={{ 
                bgcolor: '#FF9F7E',
                '&:hover': { bgcolor: '#E58E6E' },
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Draw
            </Button>
            <Button
              variant="contained"
              startIcon={<MicIcon />}
              onClick={() => startCreating('audio')}
              sx={{ 
                bgcolor: '#4A306D',
                '&:hover': { bgcolor: '#3A2057' },
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Record Audio
            </Button>
            <Button
              variant="contained"
              startIcon={<VideocamIcon />}
              onClick={() => startCreating('dancing')}
              sx={{ 
                bgcolor: '#8FDEB4',
                '&:hover': { bgcolor: '#7FCBA3' },
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Dancing
            </Button>
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={() => startCreating('imaging')}
              sx={{ 
                bgcolor: '#00BCD4',
                '&:hover': { bgcolor: '#00ACC1' },
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Imaging
            </Button>
          </Box>
        )}
        
        {/* Creation Form */}
        {isCreating && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, md: 3 }, 
              mb: { xs: 3, md: 4 }, 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'divider' 
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1.5, sm: 0 },
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 400, fontFamily: '"Lato", sans-serif' }}>
                {creationType === 'writing' ? 'Write' : creationType === 'drawing' ? 'Draw' : creationType === 'dancing' ? 'Dance' : creationType === 'imaging' ? 'Imaging' : 'Record Audio'}
              </Typography>
              {creationType === 'dancing' && (
                <Typography gutterBottom sx={{ fontWeight: 200, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                  Tips: Refresh the webpage to shutdown the camera if it is not shut down already
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Button 
                  variant="outlined" 
                  onClick={cancelCreating}
                  sx={{ borderRadius: 2, width: { xs: '100%', sm: 'auto' } }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={creationType === 'imaging' ? <UploadIcon /> : <SaveIcon />}
                  onClick={creationType === 'imaging' ? downloadImaging : saveCraft}
                  sx={{ 
                    bgcolor: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                    background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {creationType === 'imaging' ? 'Download' : 'Save'}
                </Button>
              </Box>
            </Box>
            
            <TextField
              fullWidth
              label="Title"
              variant="outlined"
              value={newCraft.title}
              onChange={(e) => setNewCraft({ ...newCraft, title: e.target.value })}
              sx={{ mb: 3 }}
            />
            
            {/* Writing Editor */}
            {creationType === 'writing' && (
              <TextField
                fullWidth
                multiline
                rows={10}
                placeholder="Start writing here..."
                value={newCraft.content}
                onChange={(e) => setNewCraft({ ...newCraft, content: e.target.value })}
                sx={{ mb: 3 }}
              />
            )}
            
            {/* Drawing Canvas */}
            {creationType === 'drawing' && (
              <>
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                      Brush Color
                    </Typography>
                    <input
                      type="color"
                      value={brushColor}
                      onChange={(e) => setBrushColor(e.target.value)}
                      style={{ width: 40, height: 40, padding: 0, border: 'none' }}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                      Brush Size: {brushSize}px
                    </Typography>
                    
                  </Box>
                </Box>
                <Box 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    mb: 3,
                    overflow: 'hidden',
                    backgroundColor: '#8FDEB4', // Added pale watercolor wash background color
                    backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.7), rgba(143,222,180,0.3))', // Added watercolor effect
                    minHeight: { xs: 220, sm: 320 },
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={400}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </Box>
              </>
            )}
            
            {creationType === 'imaging' && (
              <Paper sx={{ p: 2, mb: 3 }}>
                
                <Typography variant="h6" fontWeight={400} fontFamily={'"Lato", sans-serif'} gutterBottom> Describe the image you want to create to your Mood Friend (AI) </Typography>
                <TextField
                  label="Enter prompt here ... (max 1000 words)"
                  value={imagingPrompt}
                  onChange={(e) => setImagingPrompt(e.target.value)}
                  placeholder="Describe the image you want to create ..."
                  multiline
                  minRows={3}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={generateImaging}
                  startIcon={<UploadIcon />}
                  sx={{ mr: 2 }}
                >
                  Imaging
                </Button>
                {imagingImageURL && (
                  <Box mt={2}>
                    <img
                      src={imagingImageURL}
                      alt="Generated"
                      style={{ maxWidth: '100%', borderRadius: 8 }}
                    />
                  </Box>
                )}
              </Paper>
            )}
            
            {creationType === 'dancing' && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Button
                    variant={danceMode === 'watch' ? 'contained' : 'outlined'}
                    startIcon={<OndemandVideoIcon />}
                    onClick={() => setDanceMode('watch')}
                    sx={{ 
                      mr: 2,
                      borderRadius: 2,
                      ...(danceMode === 'watch' && {
                        background: 'linear-gradient(135deg, #6C8DFA 0%, #8FDEB4 100%)',
                      })
                    }}
                  >
                    Watch Dance
                  </Button>
                  <Button
                    variant={danceMode === 'self' ? 'contained' : 'outlined'}
                    startIcon={<SelfImprovementIcon />}
                    onClick={startSelfDancing}
                    sx={{ 
                      borderRadius: 2,
                      ...(danceMode === 'self' && {
                        background: 'linear-gradient(135deg, #8FDEB4 0%, #FF9F7E 100%)',
                      })
                    }}
                  >
                    Self-Dancing
                  </Button>
                </Box>
                
                {danceMode === 'watch' && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                      Watching Dance
                    </Typography>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      {danceVideos.map((video) => (
                        <Grid item xs={12} md={4} key={video.id}>
                          <Card 
                            elevation={0} 
                            sx={{ 
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                              <iframe
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: 'none',
                                }}
                                src={video.url}
                                title={video.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </Box>
                            <CardContent>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
                                {video.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                                {video.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontFamily: '"Quicksand", sans-serif' }}>
                                Duration: {video.duration}
                              </Typography>
                            </CardContent>
                            <CardActions>
                              <Button 
                                size="small" 
                                startIcon={<PlayArrowIcon />}
                                onClick={() => window.open(video.url.replace('embed/', 'watch?v='), '_blank')}
                              >
                                Watch Full Video
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {danceMode === 'self' && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 400, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
                      Self-Dancing
                    </Typography>
                    
                    {showCamera ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: '100%', 
                            maxWidth: 600, 
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 2,
                          }}
                        >
                          <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            style={{ width: '100%', height: 'auto' }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                          {!recordingVideo ? (
                            <Button
                              variant="contained"
                              startIcon={<VideocamIcon />}
                              onClick={startRecordingVideo}
                              sx={{ 
                                bgcolor: '#FF9F7E',
                                '&:hover': { bgcolor: '#E58E6E' },
                                borderRadius: 2,
                              }}
                            >
                              Start Recording
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={stopRecordingVideo}
                              sx={{ borderRadius: 2 }}
                            >
                              Stop Recording
                            </Button>
                          )}
                          
                          <Button
                            variant="outlined"
                            onClick={closeCamera}
                            sx={{ borderRadius: 2 }}
                          >
                            Close Camera
                          </Button>
                        </Box>
                        
                        {videoURL && (
                          <Box sx={{ width: '100%', maxWidth: 600, mt: 2 }}>
                            <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                              Your Dance Recording:
                            </Typography>
                            <Paper 
                              elevation={0} 
                              sx={{ 
                                p: 2, 
                                borderRadius: 2, 
                                border: '1px solid', 
                                borderColor: 'divider',
                                bgcolor: 'rgba(108, 141, 250, 0.05)',
                              }}
                            >
                              <video 
                                controls 
                                src={videoURL}
                                style={{ width: '100%' }}
                              >
                                Your browser does not support the video element.
                              </video>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button 
                                  size="small" 
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => {
                                    setVideoURL('');
                                    setNewCraft({
                                      ...newCraft,
                                      content: '',
                                    });
                                    setSnackbarMessage('Dance video deleted');
                                    setSnackbarOpen(true);
                                  }}
                                  sx={{ mr: 1 }}
                                >
                                  Delete
                                </Button>
                                <Button 
                                  size="small" 
                                  startIcon={<UploadIcon />}
                                  onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = videoURL;
                                    a.download = `dance-${new Date().toISOString()}.webm`;
                                    a.click();
                                  }}
                                >
                                  Download
                                </Button>
                              </Box>                              
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" paragraph sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                          Express yourself through movement! Click the button below to start your dance recording.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<VideocamIcon />}
                          onClick={startSelfDancing}
                          sx={{ 
                            background: 'linear-gradient(135deg, #8FDEB4 0%, #FF9F7E 100%)',
                            borderRadius: 2,
                          }}
                        >
                          Open Camera
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Audio Recording */}
            {creationType === 'audio' && (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
      <Box 
        sx={{ 
          width: 150,
          height: 150,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          background: isRecording 
            ? 'linear-gradient(135deg, #FF9F7E 0%, #FF5252 100%)' 
            : 'linear-gradient(135deg, #6C8DFA 0%, #4A306D 100%)',
          boxShadow: isRecording ? '0 0 0 10px rgba(255, 82, 82, 0.2)' : 'none',
          transition: 'all 0.3s ease',
          animation: isRecording ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              boxShadow: '0 0 0 0 rgba(255, 82, 82, 0.7)',
            },
            '70%': {
              boxShadow: '0 0 0 10px rgba(255, 82, 82, 0)',
            },
            '100%': {
              boxShadow: '0 0 0 0 rgba(255, 82, 82, 0)',
            },
          },
        }}
      >
        <IconButton
          onClick={isRecording ? stopRecording : startRecording}
          sx={{ 
            color: 'white',
            width: 80,
            height: 80,
          }}
        >
          <MicIcon sx={{ fontSize: 40 }} />
        </IconButton>
      </Box>
      
      <Typography variant="body1" sx={{ mb: 2, fontFamily: '"Quicksand", sans-serif' }}>
        {isRecording 
          ? `Recording... ${formatTime(recordingTime)}` 
          : 'Click to start recording'}
      </Typography>
      
      {audioURL && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Typography variant="body2" gutterBottom sx={{ fontFamily: '"Quicksand", sans-serif' }}>
            Your Recording:
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              bgcolor: 'rgba(108, 141, 250, 0.05)',
            }}
          >
            <audio 
              ref={audioRef}
              controls 
              src={audioURL}
              style={{ width: '100%' }}
            >
              Your browser does not support the audio element.
            </audio>
            
            {audioBlob && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                  Size: {(audioBlob.size / 1024).toFixed(2)} KB
                </Typography>
                <Button 
                  size="small" 
                  startIcon={<UploadIcon />}
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = audioURL;
                    a.download = `recording-${new Date().toISOString()}.wav`;
                    a.click();
                  }}
                >
                  Download
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  )}
            


          </Paper>
        )}
        
        {/* Crafts Tabs */}
        {!isCreating && (
          <>

<Box sx={{ mb: 4 }}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <Tabs 
      value={currentTab} 
      onChange={(e, newValue) => setCurrentTab(newValue)}
      sx={{ 
        '& .MuiTabs-indicator': { backgroundColor: '#6C8DFA' },
      }}
    >
      <Tab 
        label="My Crafts" 
        sx={{ 
          textTransform: 'none', 
          fontWeight: 500,
          '&.Mui-selected': { color: '#6C8DFA' } 
        }} 
      />

    </Tabs>
    
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button 
        variant="outlined" 
        startIcon={<FilterListIcon />}
        onClick={() => setFilterDrawerOpen(true)}
        sx={{ borderRadius: 2 }}
      >
        Filter
      </Button>
    </Box>
  </Box>
  
  {/* Search and filter bar */}
  <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
    <TextField
      placeholder="Search Craft..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{ flexGrow: 1 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: '#6C8DFA' }} />
          </InputAdornment>
        ),
      }}
    />
    
    <FormControl sx={{ minWidth: 150 }}>
      <Select
        value={CraftFilter}
        onChange={(e) => setCraftFilter(e.target.value)}
        displayEmpty
        size="small"
      >
        <MenuItem value="all">All Types</MenuItem>
        <MenuItem value="writing">Writing</MenuItem>
        <MenuItem value="drawing">Drawing</MenuItem>
        <MenuItem value="audio">Audio</MenuItem>
        <MenuItem value="dancing">Dancing</MenuItem>
        <MenuItem value="imaging">Imaging</MenuItem>
      </Select>
    </FormControl>
    
    <FormControl sx={{ minWidth: 150 }}>
      <Select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        displayEmpty
        size="small"
      >
        <MenuItem value="newest">Newest First</MenuItem>
        <MenuItem value="oldest">Oldest First</MenuItem>
        <MenuItem value="title">By Title</MenuItem>
        <MenuItem value="popular">Most Popular</MenuItem>
      </Select>
    </FormControl>
  </Box>
  
  {/* Selected tags display */}
  {selectedTags.length > 0 && (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      {selectedTags.map(tag => (
        <Chip 
          key={tag} 
          label={tag} 
          onDelete={() => setSelectedTags(prev => prev.filter(t => t !== tag))}
          sx={{ 
            bgcolor: 'rgba(108, 141, 250, 0.1)', 
            color: '#6C8DFA',
            borderRadius: 2
          }} 
        />
      ))}
      {selectedTags.length > 0 && (
        <Button 
          size="small" 
          onClick={() => setSelectedTags([])}
          sx={{ fontSize: '0.75rem' }}
        >
          Clear All
        </Button>
      )}
    </Box>
  )}
</Box>
            
            {/* Crafts Grid */}
            {currentTab === 0 && (
  <>
    {getFilteredAndSortedCrafts().length > 0 ? (
      <>
        {(() => {
          const filteredCrafts = getFilteredAndSortedCrafts(); // already sorted by `sortBy`
          const craftsToShow = filteredCrafts.slice(0, visibleCount);

          return (
            <>
              <Grid container spacing={3}>
                {craftsToShow.map((Craft) => (
                  <Grid item xs={12} sm={6} md={4} key={(Craft as {id: number}).id}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                        }
                      }}
                    >
                      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontFamily: '"Lato", sans-serif' }}>
                          {(Craft as {title: string}).title}
                        </Typography>
                        <Chip 
                          label={(Craft as {type: string}).type} 
                          size="small"
                          sx={{ 
                            bgcolor: 
                              (Craft as {type: string}).type === 'writing' ? 'rgba(108, 141, 250, 0.1)' :
                              (Craft as {type: string}).type === 'drawing' ? 'rgba(255, 159, 126, 0.1)' :
                              (Craft as {type: string}).type  === 'audio' ? 'rgba(166, 143, 219, 0.1)' :
                              (Craft as {type: string}).type  === 'dancing' ? 'rgba(143, 222, 180, 0.1)' :
                              'rgba(0, 188, 212, 0.1)',
                            color: 
                              (Craft as {type: string}).type === 'writing' ? '#6C8DFA' :
                              (Craft as {type: string}).type === 'drawing' ? '#FF9F7E' :
                              (Craft as {type: string}).type  === 'audio' ? '#A68FDB' :
                              (Craft as {type: string}).type  === 'dancing' ? '#8FDEB4' :
                              '#00BCD4',
                            textTransform: 'capitalize',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ p: 2, pt: 0, flexGrow: 1 }}>
                        {renderCraftContent(Craft)}
                      </Box>
                      
                      {/* Craft tags */}
                      <Box sx={{ px: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {(CraftTags[(Craft as {id: number}).id] || []).map((tag, idx) => (
                            <Chip 
                              key={idx} 
                              label={tag} 
                              size="small"
                              onDelete={() => handleRemoveTag((Craft as {id: number}).id, tag)}
                              sx={{ 
                                bgcolor: 'rgba(108, 141, 250, 0.05)', 
                                color: 'text.secondary',
                                borderRadius: 1,
                                fontSize: '0.7rem'
                              }} 
                            />
                          ))}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            placeholder="Add tag"
                            size="small"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddTag((Craft as {id: number}).id, newTag);
                              }
                            }}
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                height: 32,
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => handleAddTag((Craft as {id: number}).id, newTag)}
                            sx={{ color: '#6C8DFA' }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Divider />
                      
                      <CardActions sx={{ p: 2, pt: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Button 
                            size="small" 
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewCraft(Craft)}
                            sx={{ color: '#6C8DFA' }}
                          >
                            View
                          </Button>
                        </Box>
                        
                        <Box>
                          <IconButton size="small" onClick={() => deleteCraft((Craft as any)._id || (Craft as {id: number}).id)}>
                            <DeleteIcon fontSize="small" sx={{ color: '#FF9F7E' }} />
                          </IconButton>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* See More button */}
              {visibleCount < filteredCrafts.length && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setVisibleCount((prev) => prev + 9)}
                    sx={{ borderRadius: 2 }}
                  >
                    See More
                  </Button>
                </Box>
              )}
            </>
          );
        })()}
      </>
    ) : (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
          No Crafts found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: '"Quicksand", sans-serif' }}>
          Try adjusting your filters or create a new Craft
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setCraftFilter('all');
            setSearchTerm('');
            setSelectedTags([]);
          }}
          sx={{ 
            background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
            borderRadius: 2,
          }}
        >
          Reset Filters
        </Button>
      </Box>
    )}
  </>
)}

{/* Filter Drawer */}
<Drawer
  anchor="right"
  open={filterDrawerOpen}
  onClose={() => setFilterDrawerOpen(false)}
>
  <Box sx={{ width: 300, p: 3 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, fontFamily: '"Lato", sans-serif' }}>
      Filter Crafts
    </Typography>
    
    <Divider sx={{ my: 2 }} />
    
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
      Craft Type
    </Typography>
    <List disablePadding>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'all'}
          onClick={() => setCraftFilter('all')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="All Types" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'writing'}
          onClick={() => setCraftFilter('writing')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditIcon fontSize="small" sx={{ color: '#6C8DFA' }} />
          </ListItemIcon>
          <ListItemText primary="Writing" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'drawing'}
          onClick={() => setCraftFilter('drawing')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <BrushIcon fontSize="small" sx={{ color: '#FF9F7E' }} />
          </ListItemIcon>
          <ListItemText primary="Drawing" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'audio'}
          onClick={() => setCraftFilter('audio')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <MicIcon fontSize="small" sx={{ color: '#A68FDB' }} />
          </ListItemIcon>
          <ListItemText primary="Audio" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'dancing'}
          onClick={() => setCraftFilter('dancing')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <VideocamIcon fontSize="small" sx={{ color: '#8FDEB4' }} />
          </ListItemIcon>
          <ListItemText primary="Dancing" />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton 
          selected={CraftFilter === 'imaging'}
          onClick={() => setCraftFilter('imaging')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ImageIcon fontSize="small" sx={{ color: '#00BCD4' }} />
          </ListItemIcon>
          <ListItemText primary="Imaging" />
        </ListItemButton>
      </ListItem>
    </List>
    
    <Divider sx={{ my: 2 }} />
    
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}>
      Tags
    </Typography>
    <List disablePadding>
      {getAllTags().map((tag, index) => (
        <ListItem disablePadding key={index}>
          <ListItemButton 
            selected={selectedTags.includes(tag)}
            onClick={() => {
              if (selectedTags.includes(tag)) {
                setSelectedTags(prev => prev.filter(t => t !== tag));
              } else {
                setSelectedTags(prev => [...prev, tag]);
              }
            }}
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LabelIcon fontSize="small" sx={{ color: selectedTags.includes(tag) ? '#6C8DFA' : 'text.secondary' }} />
            </ListItemIcon>
            <ListItemText primary={tag} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
    
    <Divider sx={{ my: 2 }} />
    
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
      <Button 
        variant="outlined" 
        onClick={() => {
          setCraftFilter('all');
          setSelectedTags([]);
          setFilterDrawerOpen(false);
        }}
        sx={{ mr: 1 }}
      >
        Reset
      </Button>
      <Button 
        variant="contained" 
        onClick={() => setFilterDrawerOpen(false)}
        sx={{ bgcolor: '#6C8DFA' }}
      >
        Apply
      </Button>
    </Box>
  </Box>
</Drawer>
            
            {/* Empty state */}
            {Crafts.filter(Craft => 
              currentTab === 0 || 
              (currentTab === 1 && (Craft as {type: string}).type === 'writing') ||
              (currentTab === 2 && (Craft as {type: string}).type === 'drawing') ||
              (currentTab === 3 && (Craft as {type: string}).type === 'audio')
            ).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontFamily: '"Lato", sans-serif' }}>
                  No Crafts yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: '"Quicksand", sans-serif' }}>
                  Start creating to express your mood and emotions
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => startCreating('writing')}
                  sx={{ 
                    background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
                    borderRadius: 2,
                  }}
                >
                  Create Your First Craft
                  </Button>
                </Box>
            )}
          </>
        )}
      </Container>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      <FilterDrawer />
      
      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCraft && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 500, 
              fontFamily: '"Lato", sans-serif',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {selectedCraft.title}
              <Chip 
                label={selectedCraft.type} 
                size="small"
                sx={{ 
                  bgcolor: 
                    selectedCraft.type === 'writing' ? 'rgba(108, 141, 250, 0.1)' :
                    selectedCraft.type === 'drawing' ? 'rgba(255, 159, 126, 0.1)' :
                    selectedCraft.type === 'audio' ? 'rgba(166, 143, 219, 0.1)' :
                    selectedCraft.type === 'dancing' ? 'rgba(143, 222, 180, 0.1)' :
                    'rgba(0, 188, 212, 0.1)',
                  color: 
                    selectedCraft.type === 'writing' ? '#6C8DFA' :
                    selectedCraft.type === 'drawing' ? '#FF9F7E' :
                    selectedCraft.type === 'audio' ? '#A68FDB' :
                    selectedCraft.type === 'dancing' ? '#8FDEB4' :
                    '#00BCD4',
                  textTransform: 'capitalize',
                  borderRadius: 1
                }}
              />
              <IconButton
                edge="end"
                onClick={() => setDetailDialogOpen(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontFamily: '"Quicksand", sans-serif' }}>
                  Created: {new Date(selectedCraft.createdAt).toLocaleString()}
                </Typography>
                
                {/* Display content based on craft type */}
                {selectedCraft.type === 'writing' && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: '"Quicksand", sans-serif' }}>
                    {selectedCraft.content}
                  </Typography>
                )}
                
                {selectedCraft.type === 'drawing' && (
                  <Box 
                    component="img" 
                    src={selectedCraft.content} 
                    alt={selectedCraft.title}
                    sx={{ 
                      width: '100%', 
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                )}
                
                {selectedCraft.type === 'imaging' && (
                  <Box 
                    component="img" 
                    src={selectedCraft.content} 
                    alt={selectedCraft.title}
                    sx={{ 
                      width: '100%', 
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                )}
                
                {selectedCraft.type === 'audio' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <audio controls src={selectedCraft.content} style={{ width: '100%' }}>
                      Your browser does not support the audio element.
                    </audio>
                  </Box>
                )}
                
                {selectedCraft.type === 'dancing' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <video controls src={selectedCraft.content} style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }}>
                      Your browser does not support the video element.
                    </video>
                  </Box>
                )}
              </Box>
              
              {/* Display tags */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, fontFamily: '"Lato", sans-serif' }}>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(CraftTags[selectedCraft.id] || []).map((tag, idx) => (
                    <Chip 
                      key={idx} 
                      label={tag} 
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(108, 141, 250, 0.05)', 
                        color: 'text.secondary',
                        borderRadius: 1,
                        fontSize: '0.7rem'
                      }} 
                    />
                  ))}
                  {(CraftTags[selectedCraft.id] || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
                      No tags added
                    </Typography>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default CreativeOutlet;

